import { useState } from 'react';
import { StickyNote, MoreHorizontal, ChevronUp, ChevronDown, Flag, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ListPaginationFooter } from '@/shared/ui/molecules/list-pagination-footer';
import { StatusBadge, type AppointmentStatusType } from '@/components/atoms';
import { PatientAvatar } from '@/shared/ui/components';
import { ROW_ICON_BUTTON } from '@/components/blocks/row-actions';

/**
 * AppointmentListView — Block
 * Production-grade tabular appointment list.
 *
 * Design contract (Blue Depth v2.2):
 *   - Typographic hierarchy:  patient > reason > time/doctor > room
 *   - Status colour is clinical, never decorative
 *   - Solid pill   = active patient present  (arrived, confirmed, checkedIn, fulfilled)
 *   - Outline pill = future / pending        (booked, waitlisted/proposed)
 *   - Plain text   = resolved / passive      (noShow, cancelled)
 *   - Selected row = 3 px left accent + surface-active bg
 *   - Time always sorts chronologically (parsed, not lexicographic)
 */

export interface ListAppointment {
  id: string;
  timeRange: string;
  patientName: string;
  doctorName: string;
  room?: string;
  reasonForVisit?: string;
  status: AppointmentStatusType;
  billingStatus?: 'Paid in full' | 'Partially paid' | 'Not paid' | '';
  hasNote?: boolean;
  hasFlag?: boolean;
}

type SortCol = 'time' | 'patient' | 'doctor' | 'room' | 'reason' | 'status';
type SortDir = 'asc' | 'desc';

// ─── Time parser ──────────────────────────────────────────────────────────────
// Converts "9:30a", "12:00p" → minutes since midnight for true chrono sort

function timeToMinutes(t: string): number {
  const m = t.trim().match(/^(\d{1,2}):(\d{2})(a|p)$/i);
  if (!m) return 0;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const pm = m[3].toLowerCase() === 'p';
  if (pm && h !== 12) h += 12;
  if (!pm && h === 12) h = 0;
  return h * 60 + min;
}

function rangeStartMin(range: string) {
  return timeToMinutes(range.split('-')[0]);
}

// Status badge — now the canonical `StatusBadge` atom (imported above). The
// former local STATUS_MAP / BADGE_CLASS / StatusBadge were removed in the
// system-wide status-colour unification; the atom is the single source.

// ─── Billing cell ─────────────────────────────────────────────────────────────

function BillingCell({ value }: { value?: string }) {
  if (!value) return <span className="text-sm text-text-hint select-none">—</span>;
  const cls =
    value === 'Paid in full'   ? 'text-[var(--color-status-success-text)]' :
    value === 'Partially paid' ? 'text-[var(--color-status-warning-text)]' :
    value === 'Not paid'       ? 'text-status-error'   : 'text-text-muted';
  return <span className={cn('text-sm font-medium whitespace-nowrap', cls)}>{value}</span>;
}

// ─── Sort header ──────────────────────────────────────────────────────────────

function Th({
  col, label, sort, onSort, className,
}: {
  col: SortCol;
  label: string;
  sort: { col: SortCol; dir: SortDir };
  onSort: (c: SortCol) => void;
  className?: string;
}) {
  const active = sort.col === col;
  return (
    <th className={cn('text-left align-middle', className)}>
      <button
        onClick={() => onSort(col)}
        className={cn(
          'flex items-center gap-1 whitespace-nowrap transition-colors',
          active ? 'text-brand-primary' : 'hover:text-text-primary',
        )}
      >
        {label}
        {active ? (
          sort.dir === 'asc'
            ? <ChevronUp className="h-3 w-3 shrink-0" />
            : <ChevronDown className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronDown className="h-3 w-3 shrink-0 opacity-20" />
        )}
      </button>
    </th>
  );
}

// ─── Summary bar ─────────────────────────────────────────────────────────────

function SummaryBar({
  waitingRoom, noShow, cancelled, currentTime, flaggedCount,
}: {
  waitingRoom: number;
  noShow: number;
  cancelled: number;
  currentTime?: string;
  flaggedCount: number;
}) {
  return (
    <div className="flex items-center gap-0 px-0 h-10 bg-surface-card border-b border-border-default flex-shrink-0">
      {/* Live clock */}
      {currentTime && (
        <div className="flex items-center gap-2 px-6 border-r border-border-default h-full">
          <Circle className="h-2 w-2 fill-status-error text-status-error animate-pulse" />
          <span className="text-xs font-semibold text-status-error whitespace-nowrap">
            Now &nbsp;{currentTime}
          </span>
        </div>
      )}

      {/* Counts */}
      <div className="flex items-center divide-x divide-border-default h-full">
        {[
          { label: 'Waiting Room', count: waitingRoom, countCls: 'text-brand-primary' },
          { label: 'No Show',      count: noShow,      countCls: 'text-status-error'  },
          { label: 'Cancelled',    count: cancelled,   countCls: 'text-text-muted'    },
        ].map(({ label, count, countCls }) => (
          <div key={label} className="flex items-center gap-2 px-6 h-full">
            <span className="text-xs text-text-muted">{label}</span>
            <span className={cn('text-sm font-bold tabular-nums', countCls)}>{count}</span>
          </div>
        ))}
      </div>

      <div className="flex-1" />

      {/* Flagged count */}
      {flaggedCount > 0 && (
        <div className="flex items-center gap-2 px-6 text-xs text-[var(--color-status-warning-text)] font-medium">
          <Flag className="h-3 w-3" />
          <span>{flaggedCount} flagged</span>
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

interface AppointmentListViewProps {
  appointments: ListAppointment[];
  waitingRoom?: number;
  noShow?: number;
  cancelled?: number;
  currentTime?: string;
  onAppointmentClick?: (id: string) => void;
}

const PAGE_SIZES = [10, 20, 50] as const;

export function AppointmentListView({
  appointments,
  waitingRoom = 0,
  noShow = 0,
  cancelled = 0,
  currentTime,
  onAppointmentClick,
}: AppointmentListViewProps) {
  const [sort, setSort]       = useState<{ col: SortCol; dir: SortDir }>({ col: 'time', dir: 'asc' });
  const [selectedId, setId]   = useState<string | null>(null);
  const [pageSize, setPageSz] = useState<(typeof PAGE_SIZES)[number]>(10);
  const [page, setPage]     = useState(1);

  // A different list (another day) starts on page 1. The list is compared by its appointment ids, not by the array
  // object, so a caller that builds a new array every render (an inline `.filter`) does not reset the page.
  // Adjusting state during render (not in an effect) re-renders before paint, so the user never sees page 3 of a
  // different list.
  const listKey = appointments.map((a) => a.id).join('|');
  const [listFor, setListFor] = useState(listKey);
  if (listFor !== listKey) {
    setListFor(listKey);
    setPage(1);
  }

  const handleSort = (col: SortCol) => {
    setSort((p) => p.col === col ? { col, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    setPage(1);
  };

  const sorted = [...appointments].sort((a, b) => {
    const d = sort.dir === 'asc' ? 1 : -1;
    switch (sort.col) {
      case 'time':    return (rangeStartMin(a.timeRange) - rangeStartMin(b.timeRange)) * d;
      case 'patient': return a.patientName.localeCompare(b.patientName) * d;
      case 'doctor':  return a.doctorName.localeCompare(b.doctorName) * d;
      case 'room':    return (a.room ?? '').localeCompare(b.room ?? '') * d;
      case 'reason':  return (a.reasonForVisit ?? '').localeCompare(b.reasonForVisit ?? '') * d;
      case 'status':  return a.status.localeCompare(b.status) * d;
      default:        return 0;
    }
  });

  const flaggedCount = appointments.filter((a) => a.hasFlag).length;
  const totalPages   = Math.max(1, Math.ceil(appointments.length / pageSize));
  const safePage     = Math.min(page, totalPages);
  const paged        = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-[var(--color-surface-card)]">
      <SummaryBar
        waitingRoom={waitingRoom}
        noShow={noShow}
        cancelled={cancelled}
        currentTime={currentTime}
        flaggedCount={flaggedCount}
      />

      {/* ── Scrollable table area ────────────────────────────────────── */}
      {/* The list is one card inset by the page padding (the list-table standard, src/index.css).
          `overflow: clip` on the card instead of the standard's `hidden`: a clipped card is not a
          scroll container, so the sticky header keeps sticking to this scroll region. */}
      <div className="flex-1 min-h-0 overflow-auto scrollbar-hide px-[var(--page-pad)] pt-4 pb-[var(--page-pad)]">
        <section className="list-table">
        <table className="w-full border-collapse table-fixed">

          {/* Column widths — deliberate, locked */}
          <colgroup>
            <col style={{ width: 148 }} />  {/* TIME */}
            <col style={{ width: '22%' }} /> {/* PATIENT */}
            <col style={{ width: '18%' }} /> {/* DOCTOR */}
            <col style={{ width: 96 }} />   {/* ROOM */}
            <col />                          {/* REASON — fills remaining */}
            <col style={{ width: 126 }} />  {/* STATUS */}
            <col style={{ width: 130 }} />  {/* BILLING */}
            <col style={{ width: 52 }} />   {/* NOTE */}
            <col style={{ width: 40 }} />   {/* ACTIONS */}
          </colgroup>

          {/* Sticky header */}
          <thead className="sticky top-0 z-10">
            <tr>
              <Th col="time"    label="Time"             sort={sort} onSort={handleSort} />
              <Th col="patient" label="Patient"          sort={sort} onSort={handleSort} />
              <Th col="doctor"  label="Doctor"           sort={sort} onSort={handleSort} />
              <Th col="room"    label="Room"             sort={sort} onSort={handleSort} />
              <Th col="reason"  label="Reason for Visit" sort={sort} onSort={handleSort} />
              <Th col="status"  label="Status"           sort={sort} onSort={handleSort} />
              <th className="text-left align-middle">
                <span>Billing</span>
              </th>
              <th className="align-middle text-center">
                <span>Note</span>
              </th>
              <th />
            </tr>
          </thead>

          <tbody>
            {paged.map((apt) => {
              const isSelected = selectedId === apt.id;

              return (
                <tr
                  key={apt.id}
                  onClick={() => { setId(apt.id); onAppointmentClick?.(apt.id); }}
                  className={cn(
                    'cursor-pointer group transition-colors',
                    isSelected ? 'bg-blue-100' : 'hover:bg-grey-100/60',
                  )}
                >
                  {/* Selected accent */}
                  <td
                    className={cn(
                      'pl-6 pr-4 align-middle',
                      isSelected && 'border-l-[3px] border-l-brand-primary',
                    )}
                  >
                    <span className="text-sm font-medium text-text-secondary tabular-nums whitespace-nowrap">
                      {apt.timeRange}
                    </span>
                  </td>

                  {/* Patient */}
                  <td className="px-4 align-middle">
                    <div className="flex items-center gap-3 min-w-0">
                      <PatientAvatar name={apt.patientName} size="md" />
                      <span className="text-sm font-medium text-text-primary truncate leading-tight">
                        {apt.patientName}
                      </span>
                      {/* Flag tint = brand-primary (Flutter teal #00B8E3 →
                          Signal Blue/500 anchor), not status-warning orange. */}
                      {apt.hasFlag && (
                        <Flag className="h-3 w-3 text-brand-primary shrink-0" />
                      )}
                    </div>
                  </td>

                  {/* Doctor */}
                  <td className="px-4 align-middle">
                    <div className="flex items-center gap-2 min-w-0">
                      <PatientAvatar name={apt.doctorName} variant="provider" size="sm" />
                      <span className="text-sm text-text-secondary truncate">
                        {apt.doctorName}
                      </span>
                    </div>
                  </td>

                  {/* Room */}
                  <td className="px-4 align-middle">
                    <span className="text-sm text-text-muted">{apt.room ?? '—'}</span>
                  </td>

                  {/* Reason */}
                  <td className="px-4 align-middle">
                    <span className="text-sm text-text-primary truncate block">{apt.reasonForVisit ?? '—'}</span>
                  </td>

                  {/* Status */}
                  <td className="px-4 align-middle">
                    <StatusBadge status={apt.status} />
                  </td>

                  {/* Billing */}
                  <td className="px-4 align-middle">
                    <BillingCell value={apt.billingStatus} />
                  </td>

                  {/* Note */}
                  <td className="px-4 align-middle text-center">
                    {apt.hasNote ? (
                      <StickyNote
                        className="h-[16px] w-[16px] text-text-muted inline-block"
                        strokeWidth={1.5}
                      />
                    ) : (
                      <span className="text-sm text-text-hint select-none">—</span>
                    )}
                  </td>

                  {/* ⋯ actions — invisible until row hover */}
                  <td className="pr-2 align-middle text-right">
                    <button
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="More actions"
                      className={`${ROW_ICON_BUTTON} text-text-secondary opacity-0 group-hover:opacity-100 ml-auto`}
                    >
                      <MoreHorizontal className="size-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {appointments.length === 0 && (
          <div className="flex items-center justify-center h-40 text-sm text-text-hint">
            No appointments scheduled for this period
          </div>
        )}
        </section>
      </div>

      {/* ── Pagination ────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-surface-card">
        <ListPaginationFooter
          pageNumber={safePage}
          pageSize={pageSize}
          pageSizes={PAGE_SIZES}
          totalRecords={appointments.length}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPageSz(n);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
